import classNames from "classnames";
import React, { useId } from "react";
import usePrevious from "../lib/hooks/usePrevious";
import { EmptyTd, SCROLL_LEFT_CLASSNAME, SCROLL_RIGHT_CLASSNAME } from "./Td";
import Th from "./Th";
import Tr from "./Tr";
import Row from "./Row";
import useDistinctShape from "../lib/hooks/useDistinctShape";
import { color, zIndex } from "..";
import t from "../i18n";
import useSelectedRows, {
  SELECTABLE_ROWS_CLASSNAME,
  SELECTABLE_ROW_CHECKBOX_CLASSNAME,
} from "./useSelectedRows";
import type { Sort } from "./Sort";
import type { ColumnMetaData } from "./ColumnMetaData";
import { css } from "@linaria/core";
import { colors, fonts } from "../Theme";
import {
  BANNER_HEIGHT_CSS_VARIABLE,
  PRIMARY_HEADER_HEIGHT_CSS_VARIABLE,
  SECONDARY_HEADER_HEIGHT_CSS_VARIABLE,
} from "../Header/useHeaderHeight";

interface P<Data, Column extends string> extends React.HTMLAttributes<HTMLTableElement> {
  datas: Data[];
  metadata: { [K in Column]: ColumnMetaData<Data> };
  computeRowKey: (data: Data) => string;
  stickyHeader?: boolean;
  onRowClick?: (data: Data, e: React.MouseEvent<HTMLTableRowElement>) => void;
  onRowMouseEnter?: (data: Data, e: React.MouseEvent<HTMLTableRowElement>) => void;
  onRowMouseLeave?: (data: Data, e: React.MouseEvent<HTMLTableRowElement>) => void;
  sort?: Sort<Column>;
  onSort?: (sort: Sort<Column>) => void;
  isRowInteractive?: (data: Data) => boolean;
  loading?: boolean;
  onRowsSelection?: (datas: Data[]) => void;
  isRowSelectable?: (data: Data) => boolean;
  selectedDatas?: Data[];
  selectAllRowsEnabled?: boolean;
}

const LOADING_CLASSNAME = "loading";
const OFFSET_TOP_CLASSNAME = "offsetTop";
const STICKY_HEADER_CLASSNAME = "sticky-header";
const THEAD_STICKY_OFFSET_CSS_VARIABLE = "--thead-sticky-offset";
const TABLE_DATA_SORTED = "sorted";

const Table = <Data, Column extends string>({
  datas,
  metadata,
  computeRowKey,
  stickyHeader,
  onRowMouseEnter,
  onRowMouseLeave,
  sort,
  onSort,
  loading,
  className,
  onRowsSelection,
  onRowClick = onRowsSelection ? defaultSelectableOnRowClick : undefined,
  isRowSelectable = onRowsSelection ? defaultIsRowSelectable : undefined,
  isRowInteractive = onRowsSelection ? isRowSelectable : undefined,
  selectedDatas,
  selectAllRowsEnabled = true,
  ...tableProps
}: P<Data, Column>) => {
  const id = useId();
  const tableRef = React.useRef<HTMLTableElement>(null);
  const theadRef = React.useRef<HTMLTableSectionElement>(null);
  const tbodyRef = React.useRef<HTMLTableSectionElement>(null);
  const tfootRef = React.useRef<HTMLTableSectionElement>(null);

  metadata = useDistinctShape(metadata);
  const columns = useDistinctShape(
    React.useMemo(
      () => (Object.keys(metadata) as Column[]).filter(c => !metadata[c].hidden),
      [metadata],
    ),
  );
  const hasLeftSticky = React.useMemo(
    () =>
      Object.values<ColumnMetaData<Data>>(metadata).some(
        m => m.sticky && m?.stickyDirection !== "right",
      ),
    [metadata],
  );
  const hasRightSticky = React.useMemo(
    () =>
      Object.values<ColumnMetaData<Data>>(metadata).some(
        m => m.sticky && m?.stickyDirection === "right",
      ),
    [metadata],
  );

  const previousSort = usePrevious(sort);

  // handle scroll on sort when header is sticky-positioned
  React.useLayoutEffect(() => {
    const thead = theadRef.current;
    if (!previousSort || previousSort === sort || !thead) return;
    if (thead.offsetTop === 0) return;
    requestAnimationFrame(() => {
      window.scrollTo({
        behavior: "smooth",
        top: document.documentElement.scrollTop - thead.offsetTop,
      });
    });
  }, [sort, previousSort]);

  React.useLayoutEffect(() => {
    const tbody = tbodyRef.current;
    const thead = theadRef.current;
    const tfoot = tfootRef.current;
    const table = tableRef.current;
    if (!table || !thead || !tbody || !tfoot) throw new Error("Missing table ref");

    let src: HTMLElement = tbody;
    let scrollWidth = tbody.scrollWidth - tbody.clientWidth;
    const resizeObserver = new ResizeObserver(() => {
      scrollWidth = tbody.scrollWidth - tbody.clientWidth;
    });
    resizeObserver.observe(table);

    let left = 0;
    let theadTop = 0;
    let frameId = requestAnimationFrame(update);
    // let scrollLeft = false;
    function update() {
      if (!table || !thead || !tbody || !tfoot) return;

      if (src === thead) {
        tbody.scrollLeft = left;
        tfoot.scrollLeft = left;
      }

      if (src === tbody) {
        thead.scrollLeft = left;
        tfoot.scrollLeft = left;
      }

      if (src === tfoot) {
        thead.scrollLeft = left;
        tbody.scrollLeft = left;
      }

      const cls = table.classList;
      if (hasLeftSticky && left > 0 && !cls.contains(SCROLL_LEFT_CLASSNAME)) {
        cls.add(SCROLL_LEFT_CLASSNAME);
      }

      if (hasLeftSticky && left === 0 && cls.contains(SCROLL_LEFT_CLASSNAME)) {
        cls.remove(SCROLL_LEFT_CLASSNAME);
      }

      if (hasRightSticky && left < scrollWidth && !cls.contains(SCROLL_RIGHT_CLASSNAME)) {
        cls.add(SCROLL_RIGHT_CLASSNAME);
      }

      if (hasRightSticky && left === scrollWidth && cls.contains(SCROLL_RIGHT_CLASSNAME)) {
        cls.remove(SCROLL_RIGHT_CLASSNAME);
      }

      if (theadTop > 0 && !cls.contains(OFFSET_TOP_CLASSNAME)) {
        cls.add(OFFSET_TOP_CLASSNAME);
      }

      if (theadTop === 0 && cls.contains(OFFSET_TOP_CLASSNAME)) {
        cls.remove(OFFSET_TOP_CLASSNAME);
      }

      frameId = requestAnimationFrame(update);
    }

    thead.addEventListener("scroll", setLeft, { passive: true });
    tbody.addEventListener("scroll", setLeft, { passive: true });
    tfoot.addEventListener("scroll", setLeft, { passive: true });
    function setLeft(e: Event) {
      if (!(e.target instanceof HTMLElement)) return;
      left = e.target.scrollLeft;
      src = e.target;
    }
    window.addEventListener("scroll", setTheadTop, { passive: true });
    function setTheadTop() {
      theadTop = thead?.offsetTop || 0;
    }

    return () => {
      resizeObserver.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
      thead.removeEventListener("scroll", setLeft);
      tbody.removeEventListener("scroll", setLeft);
      tfoot.removeEventListener("scroll", setLeft);
      window.removeEventListener("scroll", setTheadTop);
    };
  }, [hasLeftSticky, hasRightSticky]);

  const { isRowSelected, readRowsSelectionFromDom, selectAllRows, selectAllStatus } =
    useSelectedRows({
      tableRef,
      computeRowKey,
      onRowsSelection,
      datas,
      selectedDatas,
    });

  const head = React.useMemo(
    () => (
      <Tr>
        {columns.map((column, index) => (
          <Th
            key={column}
            onSelectAllRows={
              index === 0 && onRowsSelection && selectAllRowsEnabled ? selectAllRows : null
            }
            selectAllStatus={selectAllStatus}
            sortable={Boolean(metadata[column].sortable)}
            onSort={onSort}
            sort={sort}
            column={column}
            defaultSortDirection={metadata[column].defaultSortDirection}
            alignment={metadata[column].alignment}
            minWidth={metadata[column].minWidth}
            maxWidth={metadata[column].maxWidth}
            width={metadata[column].width}
            sticky={metadata[column].sticky}
            stickyDirection={metadata[column].stickyDirection}
            stickyBorder={metadata[column].stickyBorder}
            diagonal={metadata[column].diagonalLabel}
            verticalSeparator={metadata[column].verticalSeparator}
            label={metadata[column].label}
          />
        ))}
      </Tr>
    ),
    [
      columns,
      metadata,
      onRowsSelection,
      onSort,
      selectAllRows,
      selectAllRowsEnabled,
      selectAllStatus,
      sort,
    ],
  );

  const getRow = React.useCallback(
    (data: Data) => (
      <Row<Data, Column>
        key={computeRowKey(data)}
        dataKey={computeRowKey(data)}
        data={data}
        metadata={metadata}
        columns={columns}
        onRowClick={onRowClick}
        onRowMouseEnter={onRowMouseEnter}
        onRowMouseLeave={onRowMouseLeave}
        isRowInteractive={isRowInteractive}
        onSelection={
          onRowsSelection && isRowSelectable && isRowSelectable(data)
            ? readRowsSelectionFromDom
            : null
        }
        isRowSelected={isRowSelected}
      />
    ),
    [
      columns,
      computeRowKey,
      isRowInteractive,
      isRowSelectable,
      isRowSelected,
      metadata,
      onRowClick,
      onRowMouseEnter,
      onRowMouseLeave,
      onRowsSelection,
      readRowsSelectionFromDom,
    ],
  );

  const body = React.useMemo(() => {
    if (datas.length) return datas.map(getRow);
    return (
      <Tr>
        <EmptyTd>{t("table.empty")}</EmptyTd>
      </Tr>
    );
  }, [datas, getRow]);

  React.useLayoutEffect(() => {
    if (!tbodyRef.current) return;
    const toSet = tbodyRef.current.querySelectorAll(`td[data-column=${sort?.column}]`);
    const toClear = tbodyRef.current.querySelectorAll(`td.${TABLE_DATA_SORTED}`);
    for (const td of toSet) td.classList.add(TABLE_DATA_SORTED);
    for (const td of toClear) td.classList.remove(TABLE_DATA_SORTED);
  }, [sort?.column]);

  // Perf debug, left for convenience
  // console.log("render Table");
  // useWhyDidYouUpdate("Table", {
  //   getRow,
  //   selectAllStatus,
  //   selectAllRows,
  //   datas,
  //   metadata,
  //   computeRowKey,
  //   stickyHeader,
  //   onRowClick,
  //   isRowInteractive,
  //   sort,
  //   onSort,
  //   loading,
  //   className,
  //   columns,
  //   head,
  //   body,
  // });
  //
  const dummyScrollData = React.useMemo(
    () =>
      Object.entries<ColumnMetaData<Data>>(metadata).filter(m =>
        columns.some(c => m[0] === c && !m[1].hidden),
      ),
    [columns, metadata],
  );

  return (
    <table
      id={id}
      ref={tableRef}
      {...tableProps}
      className={classNames(table, className, {
        [SELECTABLE_ROWS_CLASSNAME]: Boolean(onRowsSelection),
        [SCROLL_LEFT_CLASSNAME]: hasLeftSticky,
        [SCROLL_RIGHT_CLASSNAME]: hasRightSticky,
      })}
    >
      <thead
        ref={theadRef}
        className={classNames(thead, { [STICKY_HEADER_CLASSNAME]: stickyHeader })}
      >
        {head}
      </thead>
      <tbody ref={tbodyRef} className={classNames(tbody, { [LOADING_CLASSNAME]: loading })}>
        {body}
      </tbody>
      <tfoot ref={tfootRef} className={tfoot}>
        <tr className="scroller">
          {dummyScrollData.map(([key, { minWidth, maxWidth, width }]) => (
            <td
              key={key}
              style={{
                minWidth: minWidth || width || 0,
                maxWidth: maxWidth || width || "initial",
              }}
            />
          ))}
        </tr>
      </tfoot>
    </table>
  );
};

export default React.memo(Table) as typeof Table;

function defaultIsRowSelectable() {
  return true;
}

function defaultSelectableOnRowClick(_: unknown, e: React.MouseEvent<HTMLTableRowElement>) {
  const checkbox = e.currentTarget.querySelector<HTMLInputElement>(
    `.${SELECTABLE_ROW_CHECKBOX_CLASSNAME} input[type=checkbox]`,
  );
  if (!checkbox) throw new Error("Missing checkbox");
  checkbox.click();
}

const table = css`
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-anchor: none; /* prevent chrome from junking when clicking table headers */
  transition: opacity 0s linear 0.1s;
  border: 1px solid;
  border-color: ${color.titanium200};
  border-radius: 4px;
  overflow: clip;
`;

const thead = css`
  display: grid;
  overflow-x: auto;
  overflow-y: clip;
  background-color: ${color.titanium100};

  ::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none; /* firefox */

  &.${STICKY_HEADER_CLASSNAME} {
    @media screen {
      position: sticky;
      top: calc(
        var(${PRIMARY_HEADER_HEIGHT_CSS_VARIABLE}, 0px) +
          var(${SECONDARY_HEADER_HEIGHT_CSS_VARIABLE}, 0px) +
          var(${BANNER_HEIGHT_CSS_VARIABLE}, 0px) + var(${THEAD_STICKY_OFFSET_CSS_VARIABLE}, 0px)
      );
    }
    z-index: ${zIndex.sticky};

    .${OFFSET_TOP_CLASSNAME} & {
      z-index: ${zIndex.stickyFixed};
      box-shadow: 0px 5px 3px -2px ${color.shadowColor};
    }
  }
`;

const tbody = css`
  display: grid;
  overflow-x: auto;
  overflow-y: clip;
  -webkit-overflow-scrolling: touch;
  font: ${fonts.body1};
  color: ${colors.carbon900};

  ::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none; /* firefox */

  &.${LOADING_CLASSNAME} {
    opacity: 0.5;
    pointer-events: none;
  }

  :last-child {
    border-bottom-left-radius: inherit;
    border-bottom-right-radius: inherit;
  }

  tr > td.sorted {
    background-color: ${colors.titanium50};
  }
`;

const tfoot = css`
  position: sticky;
  bottom: 0;
  overflow: auto;
  z-index: 2;

  .scroller {
    display: flex;
    width: max-content;

    td {
      height: 1px;
      flex: 1 1 0;
    }
  }
`;
